import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { catchError, of, tap } from 'rxjs';

export interface CrCanton {
  name: string;
  districts: string[];
}

export interface CrProvince {
  name: string;
  cantons: CrCanton[];
}

/**
 * Catálogo de provincias/cantones/distritos de Costa Rica.
 *
 * El backend NO expone estos datos: las direcciones son texto libre
 * (decisión de producto). Para ofrecer cascade province → canton →
 * district desde la UI, llevamos el catálogo estático en cliente
 * como asset JSON.
 *
 * El JSON se carga lazy desde `assets/locations/cr.json` la primera
 * vez que un consumidor llama a `ensureLoaded()`. En SSR no hace
 * fetch (sigue siendo `null` hasta hidratar), pero el cliente lanza
 * la carga en cuanto monta el componente.
 *
 * Si la carga falla, `loadError()` queda en true y el caller debe
 * caer a inputs de texto libre como fallback graceful (no bloquear
 * el checkout por un asset que no responde).
 */
@Injectable({ providedIn: 'root' })
export class LocationsService {
  private readonly http = inject(HttpClient);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly _provinces = signal<CrProvince[] | null>(null);
  private readonly _loadError = signal(false);
  private requestInFlight = false;

  readonly provinces = this._provinces.asReadonly();
  readonly loadError = this._loadError.asReadonly();
  readonly isReady = computed(() => this._provinces() !== null);

  /**
   * Lanza la carga del JSON si no está ya cargado o en vuelo.
   * Idempotente: llamarla varias veces no dispara requests duplicadas.
   * En SSR es no-op (el catálogo solo se necesita tras interacción del
   * usuario, así que no vale la pena cargarlo server-side).
   */
  ensureLoaded(): void {
    if (!this.isBrowser) return;
    if (this._provinces() !== null) return;
    if (this.requestInFlight) return;

    this.requestInFlight = true;
    this.http
      .get<CrProvince[]>('/assets/locations/cr.json')
      .pipe(
        tap((data) => this._provinces.set(data)),
        catchError(() => {
          this._loadError.set(true);
          return of(null);
        }),
      )
      .subscribe(() => {
        this.requestInFlight = false;
      });
  }

  cantonsOf(provinceName: string | null | undefined): CrCanton[] {
    if (!provinceName) return [];
    const provinces = this._provinces();
    if (!provinces) return [];
    return provinces.find((p) => p.name === provinceName)?.cantons ?? [];
  }

  districtsOf(
    provinceName: string | null | undefined,
    cantonName: string | null | undefined,
  ): string[] {
    if (!provinceName || !cantonName) return [];
    const cantons = this.cantonsOf(provinceName);
    return cantons.find((c) => c.name === cantonName)?.districts ?? [];
  }
}
